import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    inject,
    OnInit,
    signal,
    computed,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval, switchMap } from 'rxjs';

import { IncidentService } from '../services/incident.service';
import { Incident, IncidentStatus, Severity } from '../models/incident';
import { HeaderComponent } from '../components/header/header';
import { ActiveIncidentsComponent } from '../components/active-incidents/active-incidents';
import { TimelineComponent } from '../components/timeline/timeline';
import { IncidentComponent } from '../components/incident/incident';

@Component({
    selector: 'app-incidents-page',
    imports: [HeaderComponent, ActiveIncidentsComponent, TimelineComponent, IncidentComponent],
    styles: `
        nav {
            width: 100%;
            height: var(--nav-height);
            top: 0;
            left: 0;
            position: fixed;
            z-index: 1;
            background-color: var(--background-color);
            border-bottom: 1px solid #ccc;
        }
        .nav-inner {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
        }
        .logo {
            width: 140px;
            height: 30px;
            display: block;
        }
    `,
    template: `
        <nav>
            <div class="centered nav-inner">
                <a href="/" aria-label="Navigate home">
                    <img class="logo" src="assets/logo_blue_black.svg" alt="" aria-hidden="true" />
                </a>
            </div>
        </nav>
        <app-header [incidents]="activeIncidents()"></app-header>

        @if (incidents().length || selectedIncident()) {
            <main class="centered">
                @if (selectedIncident(); as selected) {
                    <div style="margin: 5rem 0">
                        <a
                            href="/"
                            style="margin-bottom: 1rem; display: block"
                            (click)="onGoBack(); $event.preventDefault()"
                            >&#10094; Go back</a
                        >
                        <app-incident [incident]="selected"></app-incident>
                    </div>
                } @else {
                    <app-active-incidents [incidents]="activeIncidents()"></app-active-incidents>
                    <app-timeline [incidents]="incidents()" (notify)="onIncidentNotify($event)">
                    </app-timeline>
                }
            </main>
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IncidentsPageComponent implements OnInit {
    private readonly incidentService = inject(IncidentService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly destroyRef = inject(DestroyRef);

    incidents = signal<Incident[]>([]);
    selectedIncident = signal<Incident | undefined>(undefined);

    activeIncidents = computed(() =>
        this.incidents().filter(
            (incident) =>
                incident.status.toLocaleLowerCase() ===
                    IncidentStatus.OnGoing.toLocaleLowerCase() && incident.severity !== Severity.P3,
        ),
    );

    ngOnInit(): void {
        // Subscribe to query params separately (fixes nested subscription bug)
        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            const incidentId = params['id'];
            if (incidentId) {
                const incident = this.incidents().find((i) => i.id === incidentId);
                this.selectedIncident.set(incident);
            } else {
                this.selectedIncident.set(undefined);
            }
        });

        // Initial load
        this.loadIncidents();

        // Periodic refresh every 30 seconds
        interval(30000)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => this.incidentService.getIncidents()),
            )
            .subscribe((incidents) => {
                this.incidents.set(incidents);
                this.updateSelectedFromParams();
            });
    }

    onIncidentNotify(incident: Incident): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { id: incident.id },
            queryParamsHandling: 'merge',
        });
    }

    onGoBack(): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
        });
    }

    private loadIncidents(): void {
        this.incidentService
            .getIncidents()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((incidents) => {
                this.incidents.set(incidents);
                this.updateSelectedFromParams();
            });
    }

    private updateSelectedFromParams(): void {
        const incidentId = this.route.snapshot.queryParams['id'];
        if (incidentId) {
            const incident = this.incidents().find((i) => i.id === incidentId);
            this.selectedIncident.set(incident);
        }
    }
}
